import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Portal } from "react-portal";
import { FaTimes } from "react-icons/fa";
import { RootState } from "store";
import { closeNotificationDetail } from "store/notificationsSlice/index.slice";
import {
  CATEGORY_COLORS,
  CategoryGlyph,
} from "components/atoms/NotificationIcon";
import { Button } from "components/atoms/Button";
import { SanitizedHtml } from "components/atoms/SanitizedHtml";

// Customer-facing type labels. "marketing" is deliberately shown as a neutral
// "Announcement" — customers should not be told a notification is marketing.
// (Staff still see the real "Marketing" category in the admin/compose views.)
const TYPE_LABELS: Record<string, string> = {
  marketing: "Announcement",
  new_feature: "New feature",
  carrier_change: "Carrier update",
  platform_issue: "Platform issue",
};

const formatPublished = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

/**
 * Fixed frame (header / optional video hero / authored-HTML body / footer)
 * driven by the selected notification. Colors + icon derive from its type.
 */
export const NotificationDetailModal = () => {
  const dispatch = useDispatch();
  const { notifications, detailNotificationId } = useSelector(
    (state: RootState) => state.notificationsSlice
  );

  const notification =
    notifications.find((item) => item.id === detailNotificationId) ?? null;

  const close = () => dispatch(closeNotificationDetail());

  // Esc closes (the bell flyout is already closed by the time this opens).
  useEffect(() => {
    if (!notification) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  if (!notification) return null;

  const type = notification.category;
  const color = CATEGORY_COLORS[type] ?? "#889096";
  const typeLabel = TYPE_LABELS[type] ?? type;
  // Optional hero asset (one image or one video) shown above the body.
  const hero = notification.detailMedia ?? null;

  return (
    <Portal>
      <div
        onMouseDown={close}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          backgroundColor: "rgba(0,0,0,.5)",
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          onMouseDown={(event) => event.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 700,
            maxHeight: "90vh",
            overflowY: "auto",
            background: "var(--colorsGray1)",
            borderRadius: 16,
            boxShadow: "0 24px 60px rgba(0,0,0,.22)",
          }}
        >
          {/* Header (type-tinted) */}
          <div
            style={{
              position: "relative",
              padding: "22px 24px 20px",
              borderBottom: "1px solid var(--colorsGray6)",
              background: `linear-gradient(180deg, ${color}14, var(--colorsGray1))`,
            }}
          >
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              style={{
                position: "absolute",
                top: 18,
                right: 18,
                width: 30,
                height: 30,
                border: "none",
                borderRadius: 8,
                background: "var(--colorsGray3)",
                color: "var(--colorsGray11)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <FaTimes size={13} />
            </button>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 12,
                color,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".04em",
                textTransform: "uppercase",
              }}
            >
              <CategoryGlyph category={type} size={14} color={color} />
              <span>{typeLabel}</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  flex: "none",
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CategoryGlyph category={type} size={24} color="#fff" />
              </div>
              <div style={{ minWidth: 0, paddingRight: 24 }}>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: "-.01em",
                    lineHeight: 1.25,
                    color: "var(--colorsGray12)",
                  }}
                >
                  {notification.title}
                </div>
                {notification.createdDate && (
                  <div
                    style={{ fontSize: 12, color: "var(--colorsGray10)", marginTop: 2 }}
                  >
                    {formatPublished(notification.createdDate)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Hero (optional) — one video or one image */}
          {hero?.url && (
            <div style={{ padding: "18px 24px 4px" }}>
              {hero.type === "video" ? (
                <video
                  src={hero.url}
                  controls
                  style={{
                    display: "block",
                    width: "100%",
                    aspectRatio: "16 / 9",
                    borderRadius: 12,
                    background: "#000",
                  }}
                />
              ) : (
                <img
                  src={hero.url}
                  alt={hero.caption || notification.title}
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    borderRadius: 12,
                  }}
                />
              )}
              {hero.caption && (
                <div
                  style={{
                    marginTop: 7,
                    fontSize: 12.5,
                    color: "var(--colorsGray10)",
                    textAlign: "center",
                  }}
                >
                  {hero.caption}
                </div>
              )}
            </div>
          )}

          {/* Body — dynamic authored HTML */}
          <div style={{ padding: "18px 24px" }}>
            {notification.detailBody ? (
              <SanitizedHtml html={notification.detailBody} linkColor={color} />
            ) : (
              <p
                style={{
                  margin: 0,
                  fontSize: 15,
                  lineHeight: 1.6,
                  color: "var(--colorsGray11)",
                  whiteSpace: "pre-line",
                }}
              >
                {notification.body}
              </p>
            )}
          </div>

          {/* Footer — single Close, right-aligned (no CTA) */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "16px 24px",
              borderTop: "1px solid var(--colorsGray6)",
            }}
          >
            <Button colorScheme="primary" size="lg" onClick={close}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  );
};
